//
// Berechnete (mathematische) virtuelle Sensoren: Erkennung und Formel-
// Handling. Der Server markiert sie mit model "CS_Computedv1" und einer
// members-Liste im sensor.json:
//   {"model":"CS_Computedv1","members":[{"id":123,"sign":1},{"id":456,"sign":-1}]}
// Der latestdata-Baum liefert model bereits versionsbereinigt ("CS_Computed").
//

export const COMPUTED_MODEL = 'CS_Computed'

function parseJson (jsonString) {
  if (!jsonString || typeof jsonString !== 'string') return null
  try {
    const parsed = JSON.parse(jsonString)
    return (parsed && typeof parsed === 'object') ? parsed : null
  } catch (e) {
    return null
  }
}

// Erkennt einen berechneten Sensor sowohl im Monitoring-Baum (model ist
// dort schon der bereinigte Code) als auch auf Admin-Objekten (nur json).
export function isComputedSensor (sensor) {
  if (!sensor) return false
  if (sensor.model === COMPUTED_MODEL) return true
  const meta = parseJson(sensor.json)
  if (!meta || typeof meta.model !== 'string') return false
  const version = meta.model.indexOf('v')
  const model = version >= 0 ? meta.model.substring(0, version) : meta.model
  return model === COMPUTED_MODEL
}

// Members aus dem json lesen; ungültige Einträge werden verworfen.
// null bei fehlendem/kaputtem json.
export function parseMembers (jsonString) {
  const meta = parseJson(jsonString)
  if (!meta || !Array.isArray(meta.members)) return null
  const members = []
  for (const entry of meta.members) {
    if (!entry || typeof entry !== 'object') continue
    const id = Number(entry.id)
    const sign = Number(entry.sign)
    if (!Number.isFinite(id) || (sign !== 1 && sign !== -1)) continue
    const member = { id, sign }
    // optionaler Systembereich je Bestandteil (L1/L2/L3), sonst der der Ausgabe
    const systempart = Number(entry.systempart)
    if (entry.systempart != null && entry.systempart !== '' && Number.isFinite(systempart)) member.systempart = systempart
    members.push(member)
  }
  return members
}

// Standard-Messgröße je Sensortyp (Spiegel von CDCSensor.defaultComputedPair):
// Zählertypen ihr kumulierter Gesamtstand, Temperatur der Momentanwert.
export function defaultPairForType (type) {
  switch (Number(type)) {
    case 1: // Gas
    case 2: // Wasser
      return { systempart: 4, valuetype: 22 }
    case 4: // Temperatur
      return { systempart: 20, valuetype: 16 }
    case 0: // Strom
    case 3: // Wärme
    default:
      return { systempart: 4, valuetype: 9 }
  }
}

// Konfigurierte Messgröße aus dem json; fällt auf den Typ-Default zurück
// (ältere berechnete Sensoren ohne systempart/valuetype im json).
export function computedPair (sensor) {
  if (sensor && sensor.computed && Number.isFinite(Number(sensor.computed.systempart)) && Number.isFinite(Number(sensor.computed.valuetype))) {
    return { systempart: Number(sensor.computed.systempart), valuetype: Number(sensor.computed.valuetype) }
  }
  const meta = sensor ? parseJson(sensor.json) : null
  const systempart = meta ? Number(meta.systempart) : NaN
  const valuetype = meta ? Number(meta.valuetype) : NaN
  if (Number.isFinite(systempart) && Number.isFinite(valuetype)) {
    return { systempart, valuetype }
  }
  return defaultPairForType(sensor ? sensor.type : 0)
}

// Bestandteile eines Sensors aus dem Monitoring-Baum: der Server liefert
// seit 0.0.299 einen fertigen `computed`-Block (mit aufgelösten Namen) —
// den bevorzugen, sonst auf das json zurückfallen.
export function computedMembers (sensor) {
  if (sensor && sensor.computed && Array.isArray(sensor.computed.members)) {
    return sensor.computed.members
      .filter(member => member && Number.isFinite(Number(member.id)) && (member.sign === 1 || member.sign === -1))
      .map(member => {
        const entry = { id: Number(member.id), sign: member.sign, name: member.name || null }
        if (member.systempart != null) entry.systempart = Number(member.systempart)
        return entry
      })
  }
  return parseMembers(sensor ? sensor.json : null)
}

// Editor-Zeilen -> API-Payload; Zeilen ohne Sensor-Auswahl fallen raus.
export function buildMembersPayload (rows) {
  if (!Array.isArray(rows)) return []
  return rows
    .filter(row => row && row.id != null && row.id !== '' && (row.sign === 1 || row.sign === -1))
    .map(row => {
      const member = { id: Number(row.id), sign: row.sign }
      if (row.systempart != null && row.systempart !== '' && Number.isFinite(Number(row.systempart))) member.systempart = Number(row.systempart)
      return member
    })
}

// "Hauptzähler − Unterzähler 1 (L2) + PV" — nameResolver(id) liefert den
// Sensornamen oder null (dann "?"); systempartResolver(sp) optional das
// Label eines je Bestandteil gesetzten Systembereichs.
export function formulaString (members, nameResolver, systempartResolver) {
  if (!Array.isArray(members) || members.length === 0) return ''
  const parts = []
  members.forEach((member, index) => {
    let name = member.name || (nameResolver && nameResolver(member.id)) || '?'
    if (member.systempart != null && systempartResolver) {
      const label = systempartResolver(member.systempart)
      if (label) name += ' (' + label + ')'
    }
    if (index === 0) {
      parts.push(member.sign === -1 ? '− ' + name : name)
    } else {
      parts.push((member.sign === -1 ? '− ' : '+ ') + name)
    }
  })
  return parts.join(' ')
}
